import {
  type FC,
  type MouseEventHandler,
  type PropsWithChildren,
  type SVGProps,
  forwardRef,
} from 'react'

import type { TooltipProps } from '@/components/ui/tooltip'
import { Tooltip } from '@/components/ui/tooltip'
import { classNames } from '@/utils/helpers'

interface Props {
  tooltip?: string | null
  className?: string
  iconClassName?: string
  icon: FC<SVGProps<SVGElement>>
  onClick?: MouseEventHandler<HTMLOrSVGElement>
  rounded?: boolean
  tooltipOptions?: Partial<TooltipProps>
  label?: string | JSX.Element
  disabled?: boolean
  testId?: string
}

export const IconButton = forwardRef<
  HTMLButtonElement,
  PropsWithChildren<Props>
>(
  (
    {
      tooltip,
      icon: Icon,
      iconClassName,
      className,
      onClick,
      rounded,
      tooltipOptions,
      label,
      disabled,
      testId,
    },
    ref,
  ) => {
    // Get a meaningful identifier for the icon button (for Sentry breadcrumbs)
    const iconIdentifier =
      tooltip || Icon.displayName || Icon.name || 'UnknownIcon'

    const content = (
      <button
        className={classNames(
          'hover:text-primary-500 flex cursor-pointer items-center justify-center gap-2 outline-none transition-colors',
          rounded &&
            'bg-secondary-50 dark:bg-secondary-600 h-8 w-8 rounded-full',
          disabled && !tooltip && 'pointer-events-none',
          disabled && 'opacity-50',
          className,
        )}
        type="button"
        onClick={tooltip ? undefined : onClick}
        ref={ref}
        data-testid={testId}
        disabled={disabled}
      >
        <Icon
          className={iconClassName}
          data-icon-name={iconIdentifier}
        />
        {label}
      </button>
    )

    return tooltip ? (
      <Tooltip
        content={tooltip}
        contentClassName="min-w-max text-title"
        className={classNames('outline-none', disabled && 'cursor-default')}
        onClick={disabled ? undefined : onClick}
        ref={ref}
        asChild
        {...tooltipOptions}
      >
        {content}
      </Tooltip>
    ) : (
      content
    )
  },
)

IconButton.displayName = 'IconButton'
